import React from 'react';

import cx from 'classnames';
import { mergeDeepRight, path } from 'ramda';
import { Controller, FieldValues, FormProvider } from 'react-hook-form';
import { GroupBase } from 'react-select-5';

import TooltipDialog from 'components/Pages/Admin/Itinerary/Details/DetailsChronologicalMode/Event/ModalContents/TooltipDialog';
import { AModal } from 'components/shared/AModal';
import { BaseSelect } from 'components/shared/BaseFormComponents/Select';
import { ISelectOption } from 'components/shared/BaseFormComponents/Select/types';
import Button from 'components/shared/Button';
import Divider from 'components/shared/Divider';
import { FormAccordion, FormAccordionItem } from 'components/shared/FormAccordion';
import { HookCheckbox, HookInput, HookTimeInput } from 'components/shared/HookFormComponents';
import { HookWysiwyg } from 'components/shared/HookFormComponents/Wysiwyg';
import Visibility from 'components/shared/Icon/icons/Visibility';
import { Select } from 'components/shared/Select/adapters/HookForm';
import { DeleteAction } from 'components/shared/Select/components/DeleteAction';
import { OptionWithAction } from 'components/shared/Select/options/OptionWithAction';
import Text from 'components/shared/Typography/Text';
import { ETripEvent } from 'detailsv2/structure';
import { useRelocationPoints } from 'hooks/useRelocationPointMutations/relocationPoints';

import cs from '../../index.module.css';
import { ChangePagePopover } from '../../shared';
import IconPicker from '../../shared/IconPicker';
import { useEventModal } from '../../useEventModal';
import { scrollToBottomById } from '../functions';
import { SaveInfoButton, SaveInfoTooltip, useTripEventDefaultsLoader } from '../shared';
import { useTripEventSaveInfo } from '../shared/useTripEventSaveInfo';
import { DayOrDatePicker } from './DayOrDatePicker';
import { useTransferModalContent } from './functions';
import s from './index.module.css';
import { ITransferTripEventModalContentProps } from './types';

type PlaceOption = {
  label: string;
  value: string;
};

const TransferTripEventModalContent = ({ tripEvent, onSubmit }: ITransferTripEventModalContentProps) => {
  const { methods, isDisabled, openPagePreview, loadOptions, handleChangeSelect, isNeedToShowTooltip, popover } =
    useTransferModalContent(tripEvent);
  const { handleSubmit, control, watch, formState, getValues } = methods;

  const { res, modalHeader } = useEventModal({ tripEvent });

  const { saveInfoSubmitHandler, isSafeInfoDisabled, safeInfoTooltipId } = useTripEventSaveInfo({
    eventType: ETripEvent.Transfer,
    methods,
    handleSubmit: onSubmit,
  });

  const onSuccess = React.useCallback(
    (defaultValues: FieldValues) => {
      const currentValues = getValues();
      if (!!tripEvent.id && !formState.touchedFields.attachedPage) return;

      methods.reset({
        ...currentValues,
        icon: defaultValues.icon,
        data: mergeDeepRight(currentValues.data, defaultValues.data || {}),
      });
    },
    [methods, tripEvent],
  );

  const attachedPage = watch('attachedPage');

  useTripEventDefaultsLoader({ page: attachedPage, eventType: ETripEvent.Transfer, onSuccess });

  const { relocationPoints, deleteRelocationPoint, createRelocationPoint, withPointsResolve } = useRelocationPoints([
    methods.watch('data.departureFrom'),
    methods.watch('data.arrivalTo'),
  ]);

  const OptionWithDeleteMemoized = React.useCallback(
    <
      Option extends PlaceOption = PlaceOption,
      _IsMulti extends boolean = boolean,
      _Group extends GroupBase<Option> = GroupBase<Option>,
    >(
      props: React.ComponentProps<typeof OptionWithAction<Option, _IsMulti, _Group>>,
    ) => {
      const onClickDelete = (e: React.MouseEvent<HTMLDivElement>) => {
        e.stopPropagation();
        deleteRelocationPoint(props.data.value);
      };

      return <OptionWithAction {...props} action={<DeleteAction onClick={onClickDelete} />} />;
    },
    [],
  );

  const onCreateRelocationPoint = (inputValue: string) => {
    createRelocationPoint(inputValue);
    return { label: inputValue, value: inputValue };
  };

  const onSubmitWithPoints = withPointsResolve(onSubmit, [
    path(['data', 'arrivalTo']),
    path(['data', 'departureFrom']),
  ]);
  const safeSubmit = handleSubmit(onSubmitWithPoints);

  return (
    <AModal isOpen header={modalHeader} onClose={() => res.success(false)} isCloseOnEsc size="xlg">
      <FormProvider {...methods}>
        <form className={cx(cs.form, s.transferFrom)}>
          <FormAccordion>
            <FormAccordionItem header="Required Information" isRequired>
              <div>
                <div className={s.inputGroup}>
                  <Text size="regular">Departure</Text>
                  <div className={s.transferContainer}>
                    <DayOrDatePicker isStartDate />
                    <HookTimeInput name="data.departureTime" control={control} label="Time" />
                    <div>
                      <Select
                        name="data.departureFrom"
                        control={methods.control}
                        options={relocationPoints}
                        optionToString
                        label={() => 'Departing From*'}
                        creatable
                        editable
                        components={{ Option: OptionWithDeleteMemoized }}
                        onCreateOption={onCreateRelocationPoint}
                      />
                    </div>
                  </div>
                </div>

                <div className={cx(s.inputGroup, s.marginTop)}>
                  <Text size="regular">Arrival</Text>
                  <div className={s.transferContainer}>
                    <DayOrDatePicker />
                    <HookTimeInput name="data.arrivalTime" control={control} label="Time" />
                    <div>
                      <Select
                        name="data.arrivalTo"
                        control={methods.control}
                        options={relocationPoints}
                        label={() => 'Arriving To*'}
                        optionToString
                        creatable
                        editable
                        components={{
                          Option: OptionWithDeleteMemoized,
                        }}
                        onCreateOption={onCreateRelocationPoint}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <Divider />

              <div className={cs.row}>
                <div>
                  <Text isStrong size="regular">
                    Icons
                  </Text>
                  <Text color="gray-600" size="medium">
                    Optionally customize your icon,
                    <br /> because we’re awesome.
                  </Text>
                </div>
                <div>
                  <IconPicker
                    name="icon"
                    control={control}
                    eventType={tripEvent.type}
                    isImprovedUIOnEventModalsFeature
                  />
                </div>
              </div>
            </FormAccordionItem>

            <FormAccordionItem header="Details">
              <div className={s.detailsContainer}>
                <HookInput name="data.bookedBy" control={control} label="Booked By" />
                <HookInput name="data.confirmationNumber" control={control} label="Confirmation Number" />
                <HookInput name="data.provider" control={control} label="Provider" />
                <HookInput name="data.contactInfo" control={control} label="Contact Information" />
                <HookInput name="data.duration" control={control} label="Duration" />
                <HookInput name="data.vehicleType" control={control} label="Vehicle Type" />
              </div>
            </FormAccordionItem>

            <FormAccordionItem header="Notes">
              <HookWysiwyg
                name="data.notes"
                control={control}
                placeholder="Add any miscellaneous notes here. Anything that might be a reusable note should be built in your Content Library first and pulled in below."
              />
            </FormAccordionItem>

            <FormAccordionItem header="Event Title">
              <div className={s.inputGroup}>
                <Text color="gray-500" size="medium">
                  Leave this blank to show the default text or add a custom title, e.g. Transfer to the hotel for Phil
                </Text>

                <HookInput name="data.displaySettings" control={control} label="New Title" />
              </div>
            </FormAccordionItem>

            <FormAccordionItem header="Add Information From Content Library">
              <div className={s.inputGroup}>
                <Text color="gray-500" size="medium">
                  This information will appear within the detailed daily itinerary. e.g., baggage restrictions or plane
                  descriptions.
                </Text>

                <div>
                  <Controller
                    name="attachedPage"
                    control={control}
                    render={({ field: { onChange, value }, fieldState: { error } }) => {
                      return (
                        <div ref={popover.ref}>
                          <BaseSelect
                            placeholder="Search Content Library"
                            isClearable
                            //@ts-ignore
                            optionActionIcon={Visibility}
                            onOptionActionClick={openPagePreview}
                            onMenuOpen={() => scrollToBottomById('AModalBody')}
                            defaultOption={value as ISelectOption}
                            loadOptions={loadOptions}
                            onChange={handleChangeSelect(onChange)}
                            errorMessage={error?.message}
                          />
                        </div>
                      );
                    }}
                  />

                  {popover.isOpen && (
                    <ChangePagePopover
                      onClose={popover.onClose}
                      onKeep={popover.onKeep}
                      onReset={popover.onReset}
                      anchorEl={popover.ref.current}
                    />
                  )}
                </div>
                <div>
                  <HookCheckbox name="data.isCollapsible" control={methods.control} className={cs.checkboxText}>
                    Collapse content on Day page
                  </HookCheckbox>
                </div>
              </div>
            </FormAccordionItem>
          </FormAccordion>

          <div className={s.marginBottom} />

          <div className={cx(cs.footer, cs.improvedUIOnEventModalsFeature)}>
            <SaveInfoButton
              disabled={isSafeInfoDisabled}
              handleSaveForMyself={() => saveInfoSubmitHandler(false)}
              handleSaveForTeam={() => saveInfoSubmitHandler(true)}
              tooltipId={safeInfoTooltipId}
            />

            <div className={cs.rightFooter}>
              {tripEvent.id && (
                <TooltipDialog tripEventId={tripEvent.id} onSubmit={() => {}}>
                  <Button type="primary2" className={cs.buttonText} size="large" htmlType="button">
                    Remove
                  </Button>
                </TooltipDialog>
              )}

              {isNeedToShowTooltip ? (
                <TooltipDialog
                  onSubmit={safeSubmit}
                  text={
                    'Updating the Content Page here will overwrite \n the changes you had made within the itinerary.'
                  }
                  cancelButtonText="Cancel"
                  confirmButtonText="Confirm"
                >
                  <Button disabled={isDisabled} className={cs.buttonText} size="large" htmlType="button">
                    Update
                  </Button>
                </TooltipDialog>
              ) : (
                <Button
                  disabled={isDisabled}
                  onClick={safeSubmit}
                  className={cs.buttonText}
                  size="large"
                  htmlType="button"
                >
                  {tripEvent.id ? 'Update' : 'Add To Itinerary'}
                </Button>
              )}
            </div>
          </div>

          <SaveInfoTooltip tooltipId={safeInfoTooltipId} />
        </form>
      </FormProvider>
    </AModal>
  );
};

export default TransferTripEventModalContent;
